import React, {useState, useEffect, useReducer} from "react";
import { ScrubUpdateReducer, initialButtonState } from "../../commons/reducer/ScrubUpdateReducer";
import Alert from "@amzn/meridian/alert";
import Table, { TableRow, TableCell } from "@amzn/meridian/table";
import Text from "@amzn/meridian/text";
import { Translation, useTranslation } from "./Translation";
import Button from "@amzn/meridian/button";
import {
    BUTTON_DISABLE_KEY_PREFIX,
    BUTTON_DISABLE_TIME,
    SCRUB_VIEW_TYPE,
    PACKAGE_COUNT_WARNING_THRESHOLD,
    REMOVE_MISSING_BUTTON_DISABLE_KEY_PREFIX,
    REMOVE_MISSING_BUTTON_DISABLE_TIME,
    SCRUB_MARK_AS_LOST_SELECTION_METRIC,
    STATION_CODE_SCRUB_UPDATE
} from "../../commons/constants/ScrubConstants";
import { ConfirmBox } from "./ConfirmBox";
import {useLocalStorage} from "./LocalStorage";
import Checkbox from "@amzn/meridian/checkbox";
import { asyncSetter } from "../../commons/api/GetFeatureStatus";
import { FEATURES } from "../../commons/constants/CommonConstants";
import Row from "@amzn/meridian/row";
import Column from "@amzn/meridian/column";
import Select, {SelectOption} from "@amzn/meridian/select";
import Input from "@amzn/meridian/input";
import Modal from "@amzn/meridian/modal";
import Loader from "@amzn/meridian/loader";
import Link from "@amzn/meridian/link";
import {
    isIssueLinkValid,
    extractIdFromLink,
    publishCustomCountToCSM, getStationName, getStationCode
} from "../../util/CommonUtils";

const STATES = {
    IDLE: "idle",
    LOADING: "loading",
    CONFIRM: "confirm",
    SUCCESS: "success",
    ERROR: "error",
    UNAUTHORISED_USER: "unauthorised_user"
};

export const ScrubUpdateButton = ({view, count, nonMNRCount,
                                      allItems, selectiveMarkingEnabled, selectedItems,
                                      lostDaysConfiguration, isAuthorized, baseURL, removeMissingDeliverScanEnabled,
                                  missingQueueSLA, missingQueueSLAEnabled}) => {
    
    const [{status, removeMissingStatus, reviewMissingStatus,
        removeMissingStatusConfirm, enable, terminalScans,
        terminalScanTTs, removeMissingMessage,
        reviewMissingStatusConfirm, rmEnable, failedItems, metaDataInvocation}, dispatch] = useReducer(ScrubUpdateReducer, initialButtonState);
    const modalHeading = useTranslation({ stringId: SCRUB_VIEW_TYPE[view].button});
    const packageStatus = useTranslation({ stringId: SCRUB_VIEW_TYPE[view].title});
    const [storedValue, setValue] = useLocalStorage(BUTTON_DISABLE_KEY_PREFIX + view, new Date().getTime());
    const [storedRMValue, setRMValue] = useLocalStorage(REMOVE_MISSING_BUTTON_DISABLE_KEY_PREFIX, new Date().getTime());
    const [isVirtualScrubButtonDisabled, setIsVirtualScrubButtonDisabled] = useState(false)
    const [storedStationValue, setStationValue] = useLocalStorage(STATION_CODE_SCRUB_UPDATE , getStationCode().stationCode);
    const dispatchStatus = (target) => dispatch({
        type: "SET_STATUS",
        data: target
    });

    const dispatchRemoveMissingStatus = (target) => dispatch({
        type: "SET_REMOVE_MISSING_STATUS",
        data: target
    });

    const dispatchRemoveMissingMessageStatus = () => dispatch({
        type: "REMOVE_MISSING_MESSAGE"
    });

    const dispatchReviewMissingStatus = (target) => dispatch({
        type: "SET_REVIEW_MISSING_STATUS",
        data: target
    });

    const setTerminalScanForPackage = (shipment, scan) => {
        terminalScans[shipment] = scan;

        dispatch({
            type: "SET_TERMINAL_SCANS",
            data: terminalScans
        });
    }

    const setTicketIdForPackage = (shipment, ticketId) => {
        terminalScanTTs[shipment] = ticketId;
        dispatch({
            type: "SET_TERMINAL_SCAN_TT",
            data: terminalScanTTs
        })
    }

    const markPackages = () => {
        let itemsToUpdate = Object.values(allItems).map(item=>item.TRACKING_NUMBER)

        if (SCRUB_VIEW_TYPE.scrub_lost.view_name === view && selectiveMarkingEnabled) {
            publishCustomCountToCSM({
                processName: SCRUB_MARK_AS_LOST_SELECTION_METRIC,
                metricName: getStationName() + "_" + lostDaysConfiguration,
                metricValue: getBreachingSLAShipmentsForLost().length
            })
            itemsToUpdate = selectedItems.concat(getBreachingSLAShipmentsForLost().map((item) => item.TRACKING_NUMBER));
        } else if (SCRUB_VIEW_TYPE.scrub_missing.view_name === view && selectiveMarkingEnabled) {
            itemsToUpdate = getShipmentsToBeMarkedMissing();
        }

        dispatch({
            type: "MARK_PACKAGES",
            data: {
                dispatcher: dispatch,
                targetUseCase: SCRUB_VIEW_TYPE[view].targetUseCase,
                allItems : itemsToUpdate,
                allItemsObject: allItems
            }
        });
    }

    const removePackagesFromMissingQueue = () => {
        if (removeMissingStatusConfirm === STATES.LOADING) {
            return;
        }
        let itemsToUpdate = [];
        for (let i = 0; i < selectedItems.length; i++) {
            itemsToUpdate.push({
                "metaData": {
                    "TICKET_ID": terminalScanTTs[selectedItems[i]],
                    "TERMINAL_SCAN": terminalScans[selectedItems[i]]
                },
                "scannableId": selectedItems[i]
            });

            if (isInvalidTT(selectedItems[i]))
                return;
        }

        dispatch({
            type: "REMOVE_PACKAGES_FROM_MISSING_QUEUE",
            data: {
                dispatcher: dispatch,
                itemsToUpdate: itemsToUpdate
            }
        });
    }

    const updateTerminalScan = (trackingId) => {
        let item =  allItems
            .filter((item) => item.TRACKING_NUMBER === trackingId);
        dispatch({
            type: "UPDATE_SCAN",
            data: {
                dispatcher: dispatch,
                scannableIds: [trackingId],
                targetStatus: item[0].TERMINAL_SCAN_STATE,
                shipmentType: item[0].SHIPMENT_TYPE,
                scannableId: trackingId,
                reviewMissingStatusConfirm: reviewMissingStatusConfirm
            }
        });
    }

    const revertToMissingQueue = (trackingId) => {
        dispatch({
            type: "REVERT_TO_MISSING_QUEUE",
            data: {
                dispatcher: dispatch,
                itemsToUpdate: [{
                    "metaData": {},
                    "scannableId": trackingId
                }],
                reviewMissingStatusConfirm: reviewMissingStatusConfirm,
                scannableId: trackingId
            }
        });
    }

    const [showOnRoadPackagesConfirmation, setOnRoadPackagesConfirmation] = useState(false);
    const mnrPackageCount = count - nonMNRCount;
    const toggleOnRoadState = () => setOnRoadPackagesConfirmation(!showOnRoadPackagesConfirmation);
    const getConfirmBoxBody = (packageStatus) => {
        switch (packageStatus) {
            case "Missing":
                    return (
                        <Alert type="warning" size="medium">
                            <Text><Translation stringId="scrub_modal_description"
                                               placeholder={[["STATUS", packageStatus], ["X", count]]}/></Text>
                            <br/>
                            <Table headerColumns={1} showDividers={true} spacing="small">
                                <TableRow>
                                    <TableCell><Translation stringId="total_mnr_packages"/></TableCell>
                                    <TableCell>{mnrPackageCount}</TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell><Translation stringId="total_non_mnr_packages"/></TableCell>
                                    <TableCell>{nonMNRCount}</TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell><Translation stringId="total_packages"/></TableCell>
                                    <TableCell>{count}</TableCell>
                                </TableRow>
                            </Table>
                            { nonMNRCount >= PACKAGE_COUNT_WARNING_THRESHOLD && <Checkbox checked={showOnRoadPackagesConfirmation} onChange={toggleOnRoadState}>
                                <Translation stringId="show_acknowledgement_checkbox" />
                            </Checkbox> }
                        </Alert>

                    );
            default:
                return !selectiveMarkingEnabled ? getDefaultConfirmBoxBody() : getSelectiveMarkingText();
        }
    };

    const getSelectiveMarkingText = () => {
        switch (view) {
            case SCRUB_VIEW_TYPE.scrub_lost.view_name:
                return getSelectiveMarkingTextForLost();
            default:
                return getDefaultConfirmBoxBody();
        }
    }

    const getBreachingSLAShipmentsForLost = () => {
        return allItems
            .filter((item) => item.DATE_DISTANCE >= lostDaysConfiguration)
            .filter((item) => !selectedItems.includes(item.TRACKING_NUMBER));
    }

    const getShipmentsToBeMarkedMissing = () => {
        if (!missingQueueSLAEnabled) {
            let itemsToUpdate = Object.values(allItems).map(item=>item.TRACKING_NUMBER)
            let terminalScanPackages = getTerminalScanPackages();

            return itemsToUpdate.filter((item) => !terminalScanPackages.includes(item));
        }

        return Object.values(allItems).map(item=>item.TRACKING_NUMBER)
    }

    const getTerminalScanPackages = () => {
        return Object.values(allItems)
            .filter((item) => item.IS_TERMINAL_SCAN)
            .map(item => item.TRACKING_NUMBER);
    };

    const getTerminalScanPackagesWithAttributes = () => {
        return Object.values(allItems)
            .filter((item) => item.IS_TERMINAL_SCAN);
    }

    const isReviewMissingDisabled = (trackingId) => {
        return reviewMissingStatusConfirm
        && reviewMissingStatusConfirm.hasOwnProperty(trackingId)
        && (reviewMissingStatusConfirm[trackingId] === STATES.LOADING
                || reviewMissingStatusConfirm[trackingId] === true)
    }

    const getSelectiveMarkingTextForLost = () => {
        let lostPackagesBreachingSLA = getBreachingSLAShipmentsForLost();
        return (
            <div>
                <Translation stringId="scrub_modal_default_lost"
                                     placeholder={[["X", selectedItems.length]]}  />
                {lostPackagesBreachingSLA.length > 0 ?
                    <div>
                        <br/>
                        <br/>
                        <Translation stringId="scrub_modal_default_lost_configuration"
                                     placeholder={[["Y", lostDaysConfiguration]]}  />
                        <Table spacing={"small"} showStripes={true}>
                            {lostPackagesBreachingSLA.map(shipment => {
                                return <TableRow>
                                    <TableCell>{shipment.TRACKING_NUMBER}</TableCell>
                                    <TableCell>{shipment.LAST_UPDATED}</TableCell>
                                </TableRow>
                            })}
                        </Table>
                    </div>
                    : null}
            </div>
        )
    };

    const getReviewMissingQueueButton = () => {
        if (SCRUB_VIEW_TYPE.scrub_missing.view_name !== view)
            return null;

        return isAuthorized ?
            (<Button disabled={getTerminalScanPackagesWithAttributes().length === 0}
                     size="medium"
                     onClick={() => dispatchReviewMissingStatus(STATES.CONFIRM)}>
                <Translation key={status} stringId="update_terminal_scan" />
        </Button>) : null;
    }

    const getRemoveFromMissingQueueButton = () => {
        if (SCRUB_VIEW_TYPE.scrub_missing.view_name !== view)
            return null;

        return (<Button size="medium" disabled={(selectedItems && selectedItems.length === 0) || !rmEnable}
                    onClick={() => dispatchRemoveMissingStatus(STATES.CONFIRM) } >
            <Translation key={status} stringId="remove_from_missing" />
        </Button>);
    };

    const isInvalidTT = (shipment) => {
        return terminalScanTTs.hasOwnProperty(shipment)
            && !isIssueLinkValid(terminalScanTTs[shipment]);
    }

    const getRemoveFromMissingQueueBoxBody = () => {
        return (
            <div>
                {selectedItems && selectedItems.map(shipment => {
                    return (
                        <div>
                        <Row>
                            <Column width={200}>{shipment}</Column>
                            <Column width={200}>
                                <Select value={terminalScans.hasOwnProperty(shipment) ? terminalScans[shipment] : ""}
                                    onChange={(e) => setTerminalScanForPackage(shipment, e)}>
                                    {removeMissingDeliverScanEnabled ? <SelectOption key={"DELIVERED"} value={"DELIVERED"} label={"Delivered"}/> : null}
                                    <SelectOption key={"DEPARTED_FOR_FC"} value={"DEPARTED_FOR_FC"} label={"Departed For FC"}/>
                                </Select>
                            </Column>
                            <Column width={300}>
                                <Input type="text" placeholder={"TT Link"} width={300}
                                       value={terminalScanTTs.hasOwnProperty(shipment) ? terminalScanTTs[shipment] : ""}
                                       onChange={(e) => setTicketIdForPackage(shipment, e)} error={isInvalidTT(shipment)}
                                       errorMessage={isInvalidTT(shipment) ? "Invalid TT Link" : null}
                                />
                            </Column>
                        </Row>
                        <br/>
                        </div>
                    )
                })}
            </div>
        )
    }

    const getPackageShareableLink = (trackingId)  => {
        const shareablePath = "detailPage/";
        const redirectPath = new URL(baseURL);
        redirectPath.searchParams.set("shareableLink", shareablePath.concat(trackingId));
        return redirectPath;
    };

    const getUpdateTerminalScanBoxBody = () => {
        return (
            <div>
                {getTerminalScanPackagesWithAttributes() && getTerminalScanPackagesWithAttributes().map(shipment => {
                    return <div>
                        <Row>
                            <Column width={150}>
                                <Text><Link target = "_blank" rel="noreferrer noopener" href = {getPackageShareableLink(shipment.TRACKING_NUMBER)}>{shipment.TRACKING_NUMBER}</Link></Text>
                            </Column>
                            <Column  width={150}>
                                <Text>{shipment.TERMINAL_SCAN_STATE}</Text>
                            </Column>
                            <Column width={150}>
                                <Text><Link target = "_blank" rel="noreferrer noopener" href = {shipment.TERMINAL_SCAN_TT}>{extractIdFromLink(shipment.TERMINAL_SCAN_TT)}</Link></Text>
                            </Column>
                            <Column  width={200}>
                                <Button disabled={isReviewMissingDisabled(shipment.TRACKING_NUMBER)} onClick={() => revertToMissingQueue(shipment.TRACKING_NUMBER)}>Revert To Missing Queue</Button>
                            </Column>
                            <Column  width={200}>
                                <Button disabled={isReviewMissingDisabled(shipment.TRACKING_NUMBER)} onClick={() => updateTerminalScan(shipment.TRACKING_NUMBER)}>Update Terminal Scan</Button>
                            </Column>
                            <Column>
                                {reviewMissingStatusConfirm && reviewMissingStatusConfirm.hasOwnProperty(shipment.TRACKING_NUMBER) ? parseUpdateScanResponse(reviewMissingStatusConfirm[shipment.TRACKING_NUMBER]) : null}
                            </Column>
                        </Row>
                        <br/>
                    </div>
                })}
            </div>
        )
    }

    const parseUpdateScanResponse = (response) => {
        if (response === STATES.LOADING) {
            return (<Loader size={"small"} type={"circular"}></Loader>);
        } else if (response === true) {
            return (<div>&#x2714;</div>)
        } else {
            return (<div>&#x2716;</div>)
        }

    }

    const getDefaultConfirmBoxBody = () => {
        return (<Text><Translation stringId="scrub_modal_description"
                                   placeholder={[["STATUS", packageStatus], ["X", count]]}  /></Text>);
    };

    const getButtonState = () => {
        let disabled = (status === STATES.LOADING || count === 0 || !enable || isVirtualScrubButtonDisabled);
        if (selectiveMarkingEnabled) {
            if (SCRUB_VIEW_TYPE.scrub_lost.view_name === view) {
                disabled = (status === STATES.LOADING || !enable || 0 === (selectedItems.length + getBreachingSLAShipmentsForLost().length))
            }
        }

        return disabled;
    };

    useEffect(() => {
        if (status === STATES.LOADING) {
            const currentTime = new Date().getTime();
            setValue(JSON.stringify((new Date(currentTime + BUTTON_DISABLE_TIME).getTime())));
            setStationValue(getStationCode().stationCode);
        }

        if (status === STATES.ERROR && failedItems.length > 0 && missingQueueSLAEnabled) {
            // Enabling the button back
            const currentTime = new Date().getTime();
            setValue(JSON.stringify((new Date(currentTime).getTime())));
            dispatch({type: "ENABLE_BUTTON"});
        }
    }, [status]);

    useEffect(() => {
        if (removeMissingStatusConfirm === STATES.LOADING) {
            const currentTime = new Date().getTime();
            setValue(JSON.stringify((new Date(currentTime + REMOVE_MISSING_BUTTON_DISABLE_TIME).getTime())));
            setRMValue(JSON.stringify((new Date(currentTime + REMOVE_MISSING_BUTTON_DISABLE_TIME).getTime())));
        }
    }, [removeMissingStatusConfirm]);

    useEffect(() => {
        const featureName = FEATURES.IS_VIRTUAL_SCRUB_BUTTON_DISABLED;
        asyncSetter({featureName}, setIsVirtualScrubButtonDisabled);
      }, [isVirtualScrubButtonDisabled]);   
    
    useEffect(() => {
        const remainingTime = (storedValue - new Date().getTime());
        if (remainingTime >= 0 && remainingTime <= BUTTON_DISABLE_TIME && enable && storedStationValue === getStationCode().stationCode) {
            dispatch({type: "TOGGLE_BUTTON"});
            setTimeout(function () {
                dispatch({type: "ENABLE_BUTTON"})
            }, remainingTime);
        }
    });

    useEffect(() => {
        const remainingTime = (storedRMValue - new Date().getTime());
        if (remainingTime >= 0 && remainingTime <= REMOVE_MISSING_BUTTON_DISABLE_TIME && rmEnable) {
            dispatch({type: "TOGGLE_RM_BUTTON"});
            setTimeout(function () {
                dispatch({type: "ENABLE_RM_BUTTON"})
            }, remainingTime);
        }
    });

    return (
    <React.Fragment>
    {selectiveMarkingEnabled ? getReviewMissingQueueButton() : null}
    {selectiveMarkingEnabled ? getRemoveFromMissingQueueButton() : null}
    <Button size="medium" disabled={getButtonState()}
    onClick={() => dispatchStatus(STATES.CONFIRM) } >
        { (status === STATES.LOADING)
        ? ((<Translation key={status} stringId="update_in_progress" />))
        : (<Translation key={status} stringId={SCRUB_VIEW_TYPE[view].button} />)
        }
    </Button>
        {
            [STATES.SUCCESS, STATES.ERROR]
                .filter(s => s === status)
                .map(s =>
                    (missingQueueSLAEnabled && failedItems.length > 0
                        && status === STATES.ERROR ? null : <AlertMessage type={s} key={s} onClose={() => dispatchStatus(STATES.IDLE)} />))
        }
        {
            //add popup for ERROR: unauthorised user in case of SSD to mark MISSING/LOST
            [STATES.UNAUTHORISED_USER]
                .filter(s => s === status)
                .map(s => (<ConfirmBox open={status === STATES.UNAUTHORISED_USER}
                                       onCancel={() => dispatchStatus(STATES.IDLE)}
                                       confirmDisabled={true}
                                       title={modalHeading}>
                    { metaDataInvocation!==null && metaDataInvocation.messageStringId !== null &&
                        <Row alignmentHorizontal="center" alignmentVertical="center">
                            <Alert type="error" size="small"><Translation stringId={metaDataInvocation.messageStringId}/></Alert>
                        </Row>}
                </ConfirmBox>))

        }

        {missingQueueSLAEnabled && selectiveMarkingEnabled ?
            getMarkMissingFailedAlert(missingQueueSLA, failedItems, status, dispatchStatus): null}

        {removeMissingMessage !== "" ?
            getRemoveFromMissingAlert(dispatchRemoveMissingMessageStatus, removeMissingMessage)
        : null}

        <ConfirmBox
         title={modalHeading}
         open={status === STATES.CONFIRM}
         confirmDisabled={view === SCRUB_VIEW_TYPE.scrub_missing.view_name && nonMNRCount >= PACKAGE_COUNT_WARNING_THRESHOLD && !showOnRoadPackagesConfirmation}
         onConfirm={() => markPackages()}
         onCancel={() => dispatchStatus(STATES.IDLE)} >
            {getConfirmBoxBody(packageStatus)}
        </ConfirmBox>

        <Modal
            title={useTranslation({stringId: "update_terminal_scan"})}
            open={reviewMissingStatus === STATES.CONFIRM}
            onClose={() => dispatchReviewMissingStatus(STATES.IDLE)}
            describedById="modal-description" >
            <div id="modal-description">
                {getUpdateTerminalScanBoxBody()}
            </div>
        </Modal>

        <ConfirmBox
            title={useTranslation({stringId: "remove_from_missing_message"})}
            open={removeMissingStatus === STATES.CONFIRM}
            confirmDisabled={((selectedItems && selectedItems.length !== Object.values(terminalScans).length)
                || (selectedItems && selectedItems.length !== Object.values(terminalScanTTs).length)
                || removeMissingStatusConfirm === STATES.LOADING) || !rmEnable}
            onConfirm={() => {removePackagesFromMissingQueue()}}
            onCancel={() => {dispatchRemoveMissingStatus(STATES.IDLE)}}>
            {getRemoveFromMissingQueueBoxBody(selectedItems)}
        </ConfirmBox>
    </React.Fragment>
    )
};

const AlertMessage = ({type, onClose}) => {

    return ( <div className="alert-message" >
    <Alert type={type} size="medium" width="100%" onClose={onClose} >
      <Translation stringId={`scrub_alert_${type}`} />
    </Alert>
    </div>);
};

const getRemoveFromMissingAlert = (dispatchRemoveMissingMessageStatus, removeMissingMessage) => {
    return (<div className="alert-message" >
        <Alert size="medium" width="100%" type={"informational"} onClose={() => dispatchRemoveMissingMessageStatus()}>{useTranslation({stringId: removeMissingMessage})}</Alert>
    </div>)
}

const getMarkMissingFailedAlert = (missingQueueSLA, failedItems, status, dispatch) => {
    return (<Modal
        title={<Translation stringId="scrub_missing_review_queue_heading"
                            placeholder={[["X", missingQueueSLA]]}  />}
        open={status === STATES.ERROR && failedItems.length > 0}
        onClose={() => dispatch(STATES.IDLE)}
        describedById="modal-description"
        width={700}>
        <div id="modal-description">
            <Translation stringId="scrub_missing_review_queue"
                         placeholder={[["X", missingQueueSLA]]}  />
            <br/>
            <br/>
            <Table spacing={"small"} showStripes={true}>
                {failedItems.map(shipment => {
                    return (<TableRow>
                        <TableCell>{shipment}</TableCell>
                    </TableRow>)
                })}
            </Table>
        </div>
    </Modal>);
}


// WEBPACK FOOTER //
// ./src/components/scrub/ScrubUpdateButton.js